import React from "react";
import { NavLink, Outlet, Link, Navigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, MonitorSmartphone, ShieldAlert, Mail, Users,
  Bug, Network as NetworkIcon, ClipboardCheck, FileText, Settings as Cog,
  LogOut, Radar, Inbox, FlaskConical, CreditCard
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";
import { useTenant } from "@/lib/tenant";
import { useRealtime } from "@/lib/realtime";
import ForcePasswordChange from "@/components/ForcePasswordChange";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true, id: "nav-dashboard", portal: true },
  { to: "/endpoints", label: "Endpoints", icon: MonitorSmartphone, id: "nav-endpoints", portal: true },
  { to: "/incidents", label: "AI Incidents", icon: ShieldAlert, id: "nav-incidents", portal: true },
  { to: "/approvals", label: "Approval Inbox", icon: Inbox, id: "nav-approvals", badge: true },
  { to: "/email", label: "Email Security", icon: Mail, id: "nav-email" },
  { to: "/identity", label: "Identity", icon: Users, id: "nav-identity" },
  { to: "/vulnerabilities", label: "Vulnerable Apps", icon: Bug, id: "nav-vulns", portal: true },
  { to: "/network", label: "Network", icon: NetworkIcon, id: "nav-network" },
  { to: "/compliance", label: "Compliance", icon: ClipboardCheck, id: "nav-compliance", portal: true },
  { to: "/reports", label: "Reports", icon: FileText, id: "nav-reports", portal: true },
  { to: "/billing", label: "Billing", icon: CreditCard, id: "nav-billing", portal: true, customerOnly: true },
  { to: "/settings", label: "Settings", icon: Cog, id: "nav-settings", portal: true },
];

export default function Layout({ base = "/admin" }) {
  const { user, logout } = useAuth();
  const { tenants, current, switchTenant } = useTenant();
  const { connected, pendingCount } = useRealtime();
  const { pathname } = useLocation();
  const isCustomer = user?.role === "customer";
  const pendingPayment = isCustomer && !user?.demo && ["pending_payment", "suspended"].includes(current?.status);
  const suspended = current?.status === "suspended";
  if (pendingPayment && !pathname.startsWith(`${base}/billing`) && !pathname.startsWith(`${base}/settings`)) return <Navigate to={`${base}/billing`} replace />;
  const visibleNav = isCustomer ? nav.filter((n) => n.portal) : nav.filter((n) => !n.customerOnly);
  const trialDays = current?.trial_ends_at ? Math.max(0, Math.ceil((new Date(current.trial_ends_at) - Date.now()) / 86400000)) : null;

  return (
    <div className="min-h-screen flex flex-col text-zinc-100">
      {user?.demo && (
        <div data-testid="demo-banner" className="bg-amber-400 text-[#1a1200] px-4 py-2 flex flex-wrap items-center justify-between gap-2 text-xs font-semibold">
          <span className="flex items-center gap-2"><FlaskConical size={14} /> <span className="font-mono uppercase tracking-widest">DEMO DATA</span> · You are viewing the read-only CyboScan Demo Corp workspace. Nothing here is real, and actions are disabled.</span>
          <span className="flex items-center gap-3">
            <Link to="/customer/register" data-testid="demo-start-trial" className="bg-[#1a1200] text-amber-300 px-3 py-1 font-mono uppercase tracking-widest text-[10px]">Get started</Link>
            <button data-testid="demo-exit" onClick={logout} className="underline">Exit demo</button>
          </span>
        </div>
      )}
      <div className="flex-1 flex min-h-0">
      {pendingPayment && (
        <div data-testid="pending-payment-banner" className={`fixed bottom-0 inset-x-0 z-30 ${suspended ? "bg-red-400 text-[#1a0505]" : "bg-cyan-400 text-[#041418]"} px-4 py-2 text-xs font-semibold flex items-center justify-center gap-3`}>
          <CreditCard size={14} /> {suspended ? "Your subscription is inactive — re-subscribe on the Billing page to restore access." : "Choose a plan and subscribe on the Billing page to activate your workspace."} {!(user?.owner || user?.team_role === "admin") && "Ask your organisation owner to complete this step."}
        </div>
      )}
      {user?.must_change_password && <ForcePasswordChange />}
      {/* Sidebar */}
      <aside className="w-60 shrink-0 border-r hairline bg-[#191926] flex flex-col">
        <div className="h-14 border-b hairline flex items-center px-4">
          <Logo size={30} sub={isCustomer ? "Customer Portal" : "Unified AI Security"} id="sb" />
        </div>
        <nav className="flex-1 py-3">
          {visibleNav.map((n) => (
            <NavLink
              key={n.to}
              to={`${base}${n.to === "/" ? "" : n.to}`}
              end={n.exact}
              data-testid={n.id}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2 text-[13px] font-medium border-l-2 transition-[background,border-color] duration-150 ${
                  isActive
                    ? "border-cyan-400 bg-white/[0.04] text-white"
                    : "border-transparent text-zinc-400 hover:text-zinc-100 hover:bg-white/[0.02]"
                }`
              }
            >
              <n.icon size={15} />
              <span className="flex-1">{n.label}</span>
              {n.badge && pendingCount > 0 && (
                <span data-testid="approvals-badge" className="min-w-[18px] h-[18px] px-1 bg-cyan-400 text-[#15151F] text-[10px] font-bold font-mono flex items-center justify-center">{pendingCount}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t hairline text-[10px] font-mono uppercase tracking-widest text-zinc-600">
          {isCustomer ? "READ-ONLY · PORTAL" : "v1.0 · MSP EDITION"}
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Topbar */}
        <header className="h-14 border-b hairline flex items-center justify-between px-6 bg-[#15151F]">
          <div className="flex items-center gap-4">
            {isCustomer ? (
              <div data-testid="portal-tenant" className="flex items-center gap-3 border hairline px-3 py-1.5">
                <div className="w-6 h-6 bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center"><Radar size={12} className="text-cyan-300" /></div>
                <div className="text-left">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 leading-none">Your organisation</div>
                  <div className="text-[13px] font-semibold leading-none mt-1">{current?.name || "—"}</div>
                </div>
                {user?.demo ? <span data-testid="portal-demo-tag" className="ml-2 bg-amber-400 text-[#1a1200] font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5">demo</span>
                  : trialDays !== null && <span data-testid="portal-trial-tag" className={`ml-2 font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border ${trialDays <= 3 ? "border-orange-400 text-orange-300" : "border-cyan-400/50 text-cyan-300"}`}>trial · {trialDays}d left</span>}
              </div>
            ) : (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="tenant-switcher"
                  className="flex items-center gap-3 border hairline px-3 py-1.5 hover:bg-white/[0.03] transition-colors duration-150"
                >
                  <div className="w-6 h-6 bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center">
                    <Radar size={12} className="text-cyan-300" />
                  </div>
                  <div className="text-left">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 leading-none">Customer</div>
                    <div className="text-[13px] font-semibold leading-none mt-1">{current?.name || "Select tenant"}</div>
                  </div>
                  <span className="text-zinc-600 text-xs ml-2">▾</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#1B1B28] border-white/10 rounded-none min-w-[260px]">
                <DropdownMenuLabel className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">MSP Tenants</DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-white/5" />
                {tenants.map((t) => (
                  <DropdownMenuItem
                    key={t.id}
                    data-testid={`tenant-option-${t.slug}`}
                    onClick={() => switchTenant(t.id)}
                    className="rounded-none cursor-pointer focus:bg-white/5"
                  >
                    <div className="flex flex-col">
                      <span className="text-[13px] font-medium">{t.name}</span>
                      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{t.industry} · {t.endpoint_count} endpoints</span>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            )}
            <div data-testid="realtime-status" className="hidden md:flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              <span className={`w-1.5 h-1.5 ${connected ? "bg-emerald-400 blink" : "bg-orange-400"}`} /> {connected ? "LIVE · WEBSOCKET CONNECTED" : "RECONNECTING · POLLING FALLBACK"}
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu" className="flex items-center gap-2 hover:bg-white/[0.03] px-3 py-1.5 border hairline">
                <div className="w-6 h-6 bg-white/10 border hairline flex items-center justify-center text-[10px] font-bold">
                  {user?.name?.[0] || "A"}
                </div>
                <span className="text-[13px]">{user?.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#1B1B28] border-white/10 rounded-none">
              <DropdownMenuLabel className="text-[11px]">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator className="bg-white/5" />
              <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="rounded-none cursor-pointer text-red-400 focus:bg-red-950/40 focus:text-red-300">
                <LogOut size={14} className="mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        <main className="flex-1 min-w-0 overflow-auto">
          <Outlet />
        </main>
      </div>
      </div>
    </div>
  );
}
